/**
 * @file DevicePreviewContent.tsx
 * @description
 *   Main content for the Device Preview tab. Renders modal, device list,
 *   screen, and active session table. Consumes RDP, session, and navigation contexts.
 */

import { useCallback, useMemo, useState } from 'react';
import { RDP, useRDP } from '@amzn/noolpaavai-react';
import {
  CMS,
  InternalServerException,
  InvalidInputException,
  PageId,
  PageType,
  PreviewConflictException,
  ResourceNotFoundException,
  Slot,
} from '@amzn/fire-tv-campaign-automation-service';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { useSelection } from 'src/contexts/SelectionContext';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { initiatePreview, terminatePreview } from 'src/utils/clients/controlPlane/apiMethods';
import { logger } from 'src/logger';
import { useToast } from 'src/contexts/ToastContext';

import { PREVIEW_ERROR_MESSAGES, PreviewOperation, PreviewSessionStatus } from './types';
import { usePreviewSession } from './PreviewSessionContext';
import { useDevicePreviewNavigation } from './DevicePreviewNavigationContext';
import { PreviewSessionTable } from './PreviewSessionTable';
import { DevicePreviewModal, DeviceOption, StartPreviewParams } from './DevicePreviewModal';

/** Extract user-friendly error message from preview API errors */
const getPreviewErrorMessage = (err: unknown, operation: PreviewOperation): string => {
  if (err instanceof PreviewConflictException) {
    return PREVIEW_ERROR_MESSAGES.CONFLICT;
  } else if (err instanceof InvalidInputException) {
    return PREVIEW_ERROR_MESSAGES.INVALID_INPUT;
  } else if (err instanceof ResourceNotFoundException) {
    return operation === PreviewOperation.INITIATE
      ? PREVIEW_ERROR_MESSAGES.CREATIVE_NOT_FOUND
      : PREVIEW_ERROR_MESSAGES.SESSION_NOT_FOUND;
  } else if (err instanceof InternalServerException) {
    return PREVIEW_ERROR_MESSAGES.INTERNAL_ERROR;
  }

  // Fallback for Coral framework exceptions (e.g., ValidationException) not in the SDK
  const errorName = (err as { name?: string })?.name;
  if (errorName === 'ValidationException') {
    return PREVIEW_ERROR_MESSAGES.INVALID_INPUT;
  }

  return operation === PreviewOperation.INITIATE
    ? PREVIEW_ERROR_MESSAGES.GENERIC_INITIATE
    : PREVIEW_ERROR_MESSAGES.GENERIC_TERMINATE;
};

const EMPTY_DEVICE_MAP = new Map<string, any>();

export const DevicePreviewContent = () => {
  const rdpContext = useRDP();
  const { rdpState } = rdpContext;
  const deviceMap: Map<string, any> = (rdpContext as any).deviceMap ?? EMPTY_DEVICE_MAP;
  const {
    sessions,
    addSession,
    removeSession,
    updateSessionStatus,
    removeExpiredSessions,
    getOtherSessionForCustomerByDevice,
    getSession,
  } = usePreviewSession();
  const { isTestCampaignModalOpen, closeTestCampaignModal } = useDevicePreviewNavigation();
  const { marketplace, deviceFamily } = useSelection();
  const { user } = useAuthentication();
  const [isStarting, setIsStarting] = useState(false);
  const controlPlaneClient = getControlPlaneClient();
  const userAlias = user ? getUserAlias(user) : '';
  const { addToast } = useToast();

  const deviceOptions: DeviceOption[] = useMemo(
    () =>
      (rdpState.connectedDevices ?? [])
        .filter((d) => d.deviceId)
        .map((d) => {
          const deviceInfo = deviceMap?.get(d.deviceId!);
          const identifier = deviceInfo?.dsn || d.deviceId;
          return {
            name: [deviceInfo?.deviceName, identifier].filter(Boolean).join(' — '),
            value: d.deviceId!,
            customerId: d.customerId ?? undefined,
          };
        }),
    [rdpState.connectedDevices, deviceMap],
  );

  const handleStartPreview = useCallback(
    async (params: StartPreviewParams) => {
      setIsStarting(true);
      try {
        const response = await initiatePreview(controlPlaneClient, {
          requestContext: { marketPlace: marketplace, deviceFamily, actor: userAlias },
          customerId: params.customerId,
          deviceId: params.deviceId || undefined,
          previewContent: {
            cms: CMS.CONTENT_SYMPHONY,
            creativeId: params.creativeId,
            pageId: params.pageId as PageId,
            pageType: params.pageType as PageType,
            slot: params.slot as Slot,
            marketplace,
          },
          durationInSeconds: params.durationInSeconds,
        });
        const expiresAt = new Date(response.expiresAt).getTime();
        addSession({
          deviceId: params.deviceId,
          dsn: deviceMap?.get(params.deviceId)?.dsn,
          customerId: params.customerId,
          creativeId: params.creativeId,
          expiresAt,
        });
        closeTestCampaignModal();
        addToast({ description: 'Preview started successfully', variant: 'success' });
      } catch (err) {
        logger.error('InitiatePreview failed', { error: err });
        addToast({
          description: getPreviewErrorMessage(err, PreviewOperation.INITIATE),
          variant: 'danger',
        });
      } finally {
        setIsStarting(false);
      }
    },
    [
      controlPlaneClient,
      marketplace,
      deviceFamily,
      userAlias,
      addSession,
      addToast,
      closeTestCampaignModal,
      deviceMap,
    ],
  );

  const handleTerminatePreview = useCallback(
    async (deviceId: string) => {
      const session = getSession(deviceId);
      if (!session) return;

      updateSessionStatus(deviceId, PreviewSessionStatus.TERMINATING);
      try {
        const others = getOtherSessionForCustomerByDevice(deviceId);
        if (others.length === 0) {
          await terminatePreview(controlPlaneClient, {
            requestContext: { marketPlace: marketplace, deviceFamily, actor: userAlias },
            cms: CMS.CONTENT_SYMPHONY,
            sessionId: session.customerId,
          });
        }
        removeSession(deviceId);
      } catch (err) {
        logger.error('TerminatePreview failed', { error: err });
        addToast({
          description: getPreviewErrorMessage(err, PreviewOperation.TERMINATE),
          variant: 'danger',
        });
        updateSessionStatus(deviceId, PreviewSessionStatus.ACTIVE);
      }
    },
    [
      getSession,
      removeSession,
      updateSessionStatus,
      getOtherSessionForCustomerByDevice,
      controlPlaneClient,
      marketplace,
      deviceFamily,
      userAlias,
      addToast,
    ],
  );

  return (
    <>
      <DevicePreviewModal
        isVisible={isTestCampaignModalOpen}
        onClose={closeTestCampaignModal}
        onStartPreview={handleStartPreview}
        isLoading={isStarting}
        deviceFamily={deviceFamily}
        deviceOptions={deviceOptions}
      />
      <RDP
        deviceListProps={{
          stickyHeader: true,
          ...(deviceFamily ? { filter: { deviceFamily: [deviceFamily] } } : {}),
        }}
        tabs={[
          {
            id: 'preview-sessions',
            label: 'Preview Sessions',
            content: (
              <PreviewSessionTable
                sessions={sessions}
                onTerminatePreview={handleTerminatePreview}
                onMount={removeExpiredSessions}
              />
            ),
          },
        ]}
      />
    </>
  );
};
